import { fetchApi } from '@/api/client';
import type { AlarmWithEvent, AlarmAction, AlarmSeverity } from '@/types/alarm';
import type { AlarmStats } from '@/types/api';

export interface AlarmListParams {
  alarm_status?: string;
  severity?: string;
  site_id?: string;
  page?: number;
  size?: number;
}

export async function getAlarms(params?: AlarmListParams): Promise<{ items: AlarmWithEvent[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params?.alarm_status) searchParams.append('alarm_status', params.alarm_status);
  if (params?.severity) searchParams.append('severity', params.severity);
  if (params?.site_id) searchParams.append('site_id', params.site_id);
  if (params?.page) searchParams.append('page', String(params.page));
  if (params?.size) searchParams.append('size', String(params.size));
  const query = searchParams.toString() ? `?${searchParams}` : '';
  return fetchApi<{ items: AlarmWithEvent[]; total: number }>(`/api/v1/alarms${query}`);
}

export async function getAlarm(alarmId: string): Promise<AlarmWithEvent> {
  return fetchApi<AlarmWithEvent>(`/api/v1/alarms/${alarmId}`);
}

export async function getActiveAlarms(): Promise<{ items: AlarmWithEvent[]; total: number }> {
  return fetchApi<{ items: AlarmWithEvent[]; total: number }>('/api/v1/alarms/active');
}

export async function updateAlarmSeverity(alarmId: string, severity: AlarmSeverity): Promise<AlarmWithEvent> {
  return fetchApi<AlarmWithEvent>(`/api/v1/alarms/${alarmId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ severity }),
  });
}

export async function getAlarmStats(siteId?: string): Promise<AlarmStats> {
  const query = siteId ? `?site_id=${siteId}` : '';
  return fetchApi<AlarmStats>(`/api/v1/alarms/stats${query}`);
}

export async function acknowledgeAlarm(alarmId: string): Promise<AlarmWithEvent> {
  return fetchApi<AlarmWithEvent>(`/api/v1/alarms/${alarmId}/acknowledge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export async function resolveAlarm(alarmId: string, memo?: string): Promise<AlarmWithEvent> {
  return fetchApi<AlarmWithEvent>(`/api/v1/alarms/${alarmId}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ memo }),
  });
}

export async function assignAlarm(alarmId: string, assigneeId: string, memo?: string): Promise<AlarmWithEvent> {
  return fetchApi<AlarmWithEvent>(`/api/v1/alarms/${alarmId}/assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assignee_id: assigneeId, ...(memo ? { memo } : {}) }),
  });
}

export async function getAlarmActions(alarmId: string): Promise<{ alarm_id: string; actions: AlarmAction[] }> {
  return fetchApi<{ alarm_id: string; actions: AlarmAction[] }>(`/api/v1/alarms/${alarmId}/actions`);
}

export function addAlarmNote(alarmId: string, memo: string): Promise<AlarmWithEvent> {
  return fetchApi<AlarmWithEvent>(`/api/v1/alarms/${alarmId}/notes`, {
    method: 'POST', body: JSON.stringify({ memo }),
  });
}
export function submitAlarmResult(alarmId: string, memo: string): Promise<AlarmWithEvent> {
  return fetchApi<AlarmWithEvent>(`/api/v1/alarms/${alarmId}/submit`, {
    method: 'POST', body: JSON.stringify({ memo }),
  });
}
export function reopenAlarm(alarmId: string, memo?: string): Promise<AlarmWithEvent> {
  return fetchApi<AlarmWithEvent>(`/api/v1/alarms/${alarmId}/reopen`, {
    method: 'POST', body: JSON.stringify({ memo }),
  });
}

// ==================== Batch Operations ====================

export interface AlarmBatchRequest {
  alarm_ids: string[];
  memo?: string;
}

export interface AlarmBatchResponse {
  success_count: number;
  failed_count: number;
  failed_ids: string[];
}

export async function batchAcknowledgeAlarms(data: AlarmBatchRequest): Promise<AlarmBatchResponse> {
  return fetchApi<AlarmBatchResponse>('/api/v1/alarms/batch/acknowledge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function batchResolveAlarms(data: AlarmBatchRequest): Promise<AlarmBatchResponse> {
  return fetchApi<AlarmBatchResponse>('/api/v1/alarms/batch/resolve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}
